import { defaults } from "lodash-es";
import { i18n } from "@/i18n";
import { reactive } from "vue";
import { LicenseLimitReachedError } from "@core/errors/Errors";
import { useSubscriptionOrderingPopup } from "@manager/purchasing/subscriptionOrderingPopup";

export interface AlertItem<T = unknown> {
  id: number;

  type: "alert";

  title: null | string;
  text: string;
  image: null | string;
  content: AlertContentOption[];

  confirmText: string | null;
  denyText: string | null;

  resolve: (data: T) => void;
  reject: (data: T) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const alerts = reactive(new Set<AlertItem<any>>());

interface AlertOptions {
  title: string;
  text: string;
  type: string;
  confirmText?: string;
  denyText?: string;
  content?: AlertContentOption[];
}

type ContentOption = {
  class?: string;
};

type FieldOptions = ContentOption & {
  required?: boolean;
  autofocus?: boolean;
  label: string;
  name: string;
  autocomplete?: "on" | "off" | "new-password";
};

export type AlertContentOption =
  | { type: "text"; text: string } & ContentOption
  | ({
      type: "input";
      value: string | null | undefined;
      placeholder?: string;
    } & FieldOptions)
  | ({ type: "checkbox"; labelChecked?: string; value: boolean } & FieldOptions)
  | ({
      type: "password";
      user?: string;
      current?: boolean;
      value: string | null;
    } & FieldOptions)
  | ({
    type: "number";
    value: number | null;
    min?: number;
    max?: number;
    step?: number | "any";
  } & FieldOptions);

/**
 * Show an alert
 * @param options
 */
export function showAlert<T = unknown>(
  options: Partial<AlertOptions>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cancelToken: { cancel?: (resolution: any) => void } = {}
): Promise<T | false> {
  return new Promise((_resolve, _reject) => {
    function resolve(data: T) {
      alerts.delete(alert);
      _resolve(data);
    }
    function reject(data: T) {
      alerts.delete(alert);
      _reject(data);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cancelToken.cancel = function (resolution: any = false) {
      resolve(resolution);
    };

    const alert: AlertItem<T> = reactive({
      id: Date.now(),

      type: "alert",

      title: null,
      text: "",
      image: null,
      content: [],

      confirmText: i18n.t("labels.ok"),
      denyText: null,

      resolve,
      reject
    });

    Object.assign(alert, options);

    alerts.add(alert);
  });
}

/**
 * Show a small dialog with input options
 */
export function showPrompt<T = unknown>(options: Partial<AlertOptions> = {}) {
  defaults(options, {
    confirmText: i18n.t("labels.ok"),
    denyText: i18n.t("labels.abort")
  });
  return showAlert<T>(options);
}

/**
 * Show a short message to the user.
 */
export function showMessage(text: string, options: Partial<AlertOptions> = {}) {
  return showAlert({
    ...options,
    text
  });
}

/**
 * Show a short error message to the user.
 */
export function showError(
  error: string | Error,
  options: Partial<AlertOptions> = {}
) {
  let text = "";
  if (typeof error == "string") {
    text = error;
  } else {
    if (error instanceof LicenseLimitReachedError) {
      return useSubscriptionOrderingPopup().show(error);
    }
    text = error.message;
  }

  console.warn(error);

  return showAlert({
    title: i18n.t("headings.error"),
    type: "error",
    ...options,
    text
  });
}

/**
 * Show a message that can be confirmed or denied
 */
export function showConfirm(
  text: string,
  confirmText: string = i18n.t("labels.yes"),
  denyText: string = i18n.t("labels.no"),
  options: Partial<AlertOptions> = {}
) {
  return showAlert<boolean>({
    ...options,
    text,
    confirmText,
    denyText
  });
}

export function resetAlerts() {
  for (const alert of alerts) {
    alerts.delete(alert);
  }
}

export function dismissTopAlert() {
  const alert = Array.from(alerts).pop();
  if (alert) {
    alert.resolve(false);
    alerts.delete(alert);
    return true;
  }
  return false;
}